import React, { useState, useEffect, useRef } from "react";
import { Listbox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import api from "./services/api";
import axios from "axios";

interface Avatar {
  id: string;
  name: string;
  character_image_url: string;
  preview_image_url: string;
  gender: string;
  age: string;
  ethnicity: string;
  shot_type: string;
  niche: string;
  product_type: string;
  item_type: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const typeOptions = [
  { id: "product", name: "Product" },
  { id: "talking", name: "Talking Video" },
];

// Original image dimensions
const ORIGINAL_WIDTH = 576;
const ORIGINAL_HEIGHT = 1024;

// Function to calculate percentage-based coordinates
const calculateBoundingBoxStyle = (avatar: Avatar) => {
  // Default values if any coordinate is missing
  const x = avatar.x ?? 0;
  const y = avatar.y ?? 0;
  const width = avatar.width ?? 0;
  const height = avatar.height ?? 0;

  // Convert absolute pixel coordinates to percentage of original image dimensions
  const xPercent = (x / ORIGINAL_WIDTH) * 100;
  const yPercent = (y / ORIGINAL_HEIGHT) * 100;
  const widthPercent = (width / ORIGINAL_WIDTH) * 100;
  const heightPercent = (height / ORIGINAL_HEIGHT) * 100;

  return {
    left: `${xPercent}%`,
    top: `${yPercent}%`,
    width: `${widthPercent}%`,
    height: `${heightPercent}%`,
  };
};

// Mask editor modal component
interface MaskEditorModalProps {
  avatar: Avatar;
  onClose: () => void;
  onSave: (updatedAvatar: Avatar) => void;
}

const MaskEditorModal: React.FC<MaskEditorModalProps> = ({
  avatar,
  onClose,
  onSave,
}) => {
  const [mask, setMask] = useState({
    x: avatar.x ?? 0,
    y: avatar.y ?? 0,
    width: avatar.width ?? 100,
    height: avatar.height ?? 100,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [startMask, setStartMask] = useState({ ...mask });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [saving, setSaving] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Calculate scale factors between the displayed image and original dimensions
  const scaleX = imageSize.width / ORIGINAL_WIDTH;
  const scaleY = imageSize.height / ORIGINAL_HEIGHT;

  // Update image size when it loads or window resizes
  useEffect(() => {
    const updateImageSize = () => {
      if (imageRef.current) {
        const { width, height } = imageRef.current.getBoundingClientRect();
        setImageSize({ width, height });
      }
    };

    // Call once and add event listeners
    updateImageSize();
    window.addEventListener("resize", updateImageSize);

    // Create a MutationObserver to watch for style/attribute changes
    if (imageRef.current) {
      const observer = new MutationObserver(updateImageSize);
      observer.observe(imageRef.current, {
        attributes: true,
        attributeFilter: ["style", "class", "width", "height"],
      });

      // Clean up
      return () => {
        window.removeEventListener("resize", updateImageSize);
        observer.disconnect();
      };
    }

    return () => window.removeEventListener("resize", updateImageSize);
  }, []);

  const handleMouseDown = (
    e: React.MouseEvent,
    action: "drag" | "resize",
    handle?: string
  ) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling to parent elements

    if (action === "drag") {
      setIsDragging(true);
      setIsResizing(false); // Ensure resizing is off
    } else {
      setIsResizing(true);
      setIsDragging(false); // Ensure dragging is off
      setResizeHandle(handle || null);
    }

    setStartPoint({
      x: e.clientX,
      y: e.clientY,
    });
    setStartMask({ ...mask });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging && !isResizing) return;
    if (scaleX === 0 || scaleY === 0) return; // Avoid division by zero

    const deltaX = e.clientX - startPoint.x;
    const deltaY = e.clientY - startPoint.y;

    // Convert deltas to image scale
    const scaledDeltaX = deltaX / scaleX;
    const scaledDeltaY = deltaY / scaleY;

    if (isDragging) {
      // Update position while keeping within bounds
      let newX = Math.max(
        0,
        Math.min(ORIGINAL_WIDTH - mask.width, startMask.x + scaledDeltaX)
      );
      let newY = Math.max(
        0,
        Math.min(ORIGINAL_HEIGHT - mask.height, startMask.y + scaledDeltaY)
      );

      setMask({
        ...mask,
        x: Math.round(newX),
        y: Math.round(newY),
      });
    } else if (isResizing && resizeHandle) {
      let newMask = { ...startMask };

      // Handle different resize operations based on which handle was grabbed
      switch (resizeHandle) {
        case "top-left":
          newMask = {
            x: Math.max(0, startMask.x + scaledDeltaX),
            y: Math.max(0, startMask.y + scaledDeltaY),
            width: Math.max(20, startMask.width - scaledDeltaX),
            height: Math.max(20, startMask.height - scaledDeltaY),
          };
          break;
        case "top-right":
          newMask = {
            x: startMask.x,
            y: Math.max(0, startMask.y + scaledDeltaY),
            width: Math.max(20, startMask.width + scaledDeltaX),
            height: Math.max(20, startMask.height - scaledDeltaY),
          };
          break;
        case "bottom-left":
          newMask = {
            x: Math.max(0, startMask.x + scaledDeltaX),
            y: startMask.y,
            width: Math.max(20, startMask.width - scaledDeltaX),
            height: Math.max(20, startMask.height + scaledDeltaY),
          };
          break;
        case "bottom-right":
          newMask = {
            x: startMask.x,
            y: startMask.y,
            width: Math.max(20, startMask.width + scaledDeltaX),
            height: Math.max(20, startMask.height + scaledDeltaY),
          };
          break;
      }

      // Apply bounds checking
      if (newMask.x + newMask.width > ORIGINAL_WIDTH) {
        newMask.width = ORIGINAL_WIDTH - newMask.x;
      }
      if (newMask.y + newMask.height > ORIGINAL_HEIGHT) {
        newMask.height = ORIGINAL_HEIGHT - newMask.y;
      }

      setMask({
        x: Math.round(newMask.x),
        y: Math.round(newMask.y),
        width: Math.round(newMask.width),
        height: Math.round(newMask.height),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedAvatar = {
        ...avatar,
        x: mask.x,
        y: mask.y,
        width: mask.width,
        height: mask.height,
      };
      onSave(updatedAvatar);
    } catch (error) {
      console.error("Error saving mask:", error);
      toast.error("Failed to save mask changes");
    } finally {
      setSaving(false);
    }
  };

  // Only compute the mask style when we have valid image dimensions
  const maskStyle =
    imageSize.width > 0 && imageSize.height > 0
      ? {
          left: `${(mask.x / ORIGINAL_WIDTH) * 100}%`,
          top: `${(mask.y / ORIGINAL_HEIGHT) * 100}%`,
          width: `${(mask.width / ORIGINAL_WIDTH) * 100}%`,
          height: `${(mask.height / ORIGINAL_HEIGHT) * 100}%`,
        }
      : { display: "none" }; // Hide mask until image dimensions are known

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 dark:bg-opacity-80 p-4 overflow-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl flex flex-col max-h-[95vh]">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Mask: {avatar.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        <div className="p-4 flex-grow overflow-auto bg-gray-50 dark:bg-gray-900">
          <div
            ref={containerRef}
            className="relative mx-auto mb-4"
            style={{
              aspectRatio: "9/16",
              maxHeight: "60vh",
              background: "rgba(240, 240, 240, 0.5)",
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              ref={imageRef}
              src={avatar.preview_image_url || avatar.character_image_url}
              alt={avatar.name}
              className="w-full h-full object-contain"
              onLoad={() => {
                // Update image dimensions when loaded
                if (imageRef.current) {
                  const { width, height } =
                    imageRef.current.getBoundingClientRect();
                  setImageSize({ width, height });
                }
              }}
            />

            {/* Debug info - toggle with click */}
            {showDebug && (
              <div className="absolute top-0 left-0 bg-black bg-opacity-70 text-white text-xs p-1 z-20">
                Image: {imageSize.width.toFixed(0)}x
                {imageSize.height.toFixed(0)} px
                <br />
                Scale: {scaleX.toFixed(2)}x{scaleY.toFixed(2)}
              </div>
            )}

            <button
              onClick={() => setShowDebug(!showDebug)}
              className="absolute top-0 right-0 bg-gray-800 dark:bg-gray-600 text-white text-xs px-1.5 py-0.5 rounded-bl-md opacity-50 hover:opacity-100"
            >
              {showDebug ? "Hide" : "Debug"}
            </button>

            {/* Mask layer */}
            {imageSize.width > 0 && (
              <div
                className="absolute border-2 border-purple-500 dark:border-purple-400 cursor-move bg-purple-500 dark:bg-purple-400 bg-opacity-30 dark:bg-opacity-40"
                style={maskStyle}
                onMouseDown={(e) => handleMouseDown(e, "drag")}
              >
                {/* Resize handles */}
                <div
                  className="absolute -left-2 -top-2 w-5 h-5 bg-white dark:bg-gray-200 border-2 border-purple-600 dark:border-purple-500 rounded-full cursor-nw-resize z-10 pointer-events-auto"
                  onMouseDown={(e) => handleMouseDown(e, "resize", "top-left")}
                />
                <div
                  className="absolute -right-2 -top-2 w-5 h-5 bg-white dark:bg-gray-200 border-2 border-purple-600 dark:border-purple-500 rounded-full cursor-ne-resize z-10 pointer-events-auto"
                  onMouseDown={(e) => handleMouseDown(e, "resize", "top-right")}
                />
                <div
                  className="absolute -left-2 -bottom-2 w-5 h-5 bg-white dark:bg-gray-200 border-2 border-purple-600 dark:border-purple-500 rounded-full cursor-sw-resize z-10 pointer-events-auto"
                  onMouseDown={(e) =>
                    handleMouseDown(e, "resize", "bottom-left")
                  }
                />
                <div
                  className="absolute -right-2 -bottom-2 w-5 h-5 bg-white dark:bg-gray-200 border-2 border-purple-600 dark:border-purple-500 rounded-full cursor-se-resize z-10 pointer-events-auto"
                  onMouseDown={(e) =>
                    handleMouseDown(e, "resize", "bottom-right")
                  }
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Position X
              </label>
              <input
                type="number"
                value={mask.x}
                onChange={(e) =>
                  setMask({ ...mask, x: parseInt(e.target.value) || 0 })
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Position Y
              </label>
              <input
                type="number"
                value={mask.y}
                onChange={(e) =>
                  setMask({ ...mask, y: parseInt(e.target.value) || 0 })
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Width
              </label>
              <input
                type="number"
                value={mask.width}
                onChange={(e) =>
                  setMask({
                    ...mask,
                    width: Math.max(20, parseInt(e.target.value) || 0),
                  })
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Height
              </label>
              <input
                type="number"
                value={mask.height}
                onChange={(e) =>
                  setMask({
                    ...mask,
                    height: Math.max(20, parseInt(e.target.value) || 0),
                  })
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2 bg-white dark:bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewAvatarsPage: React.FC = () => {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(24); // Default number of items per page
  const [changedAvatars, setChangedAvatars] = useState<Record<string, string>>(
    {}
  );
  const [selectedType, setSelectedType] = useState("product"); // Default item type
  const [editingAvatar, setEditingAvatar] = useState<Avatar | null>(null);

  useEffect(() => {
    fetchAvatars();
  }, [page, limit, selectedType]); // Refetch when page, limit, or selectedType changes

  const fetchAvatars = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/api/avatars?page=${page}&limit=${limit}&item_type=${selectedType}`
      );
      setAvatars(response.data.avatars);
      setTotalPages(response.data.pages);
    } catch (err) {
      setError("Failed to fetch avatars");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (avatarId: string, newType: string) => {
    setChangedAvatars({
      ...changedAvatars,
      [avatarId]: newType,
    });
  };

  const handleSave = async (avatarId: string) => {
    try {
      const response = await api.patch(`/api/avatars/${avatarId}`, {
        item_type: changedAvatars[avatarId],
      });

      // Check if the update was successful
      if (response.data.success) {
        // Update the avatar in the state
        if (Array.isArray(avatars)) {
          setAvatars(
            avatars.map((avatar) =>
              avatar.id === avatarId
                ? { ...avatar, item_type: changedAvatars[avatarId] }
                : avatar
            )
          );
        }

        // Remove from changed avatars
        const newChangedAvatars = { ...changedAvatars };
        delete newChangedAvatars[avatarId];
        setChangedAvatars(newChangedAvatars);

        toast.success("Avatar updated successfully");

        // Refresh the list to ensure everything is in sync
        fetchAvatars();
      } else {
        throw new Error(response.data.message || "Failed to update avatar");
      }
    } catch (err) {
      setError("Failed to update avatar type");
      if (axios.isAxiosError(err) && err.response) {
        toast.error(err.response.data?.detail || "Failed to update avatar");
      } else {
        toast.error("Failed to update avatar");
      }
      console.error(err);
    }
  };

  const handleDelete = async (avatarId: string) => {
    if (!window.confirm("Are you sure you want to delete this avatar?")) return;

    try {
      await api.delete(`/api/avatars/${avatarId}`);

      // Remove the avatar from the state - with safety check for avatars
      if (Array.isArray(avatars)) {
        setAvatars(avatars.filter((avatar) => avatar.id !== avatarId));
      }
      toast.success("Avatar deleted successfully");
    } catch (err) {
      setError("Failed to delete avatar");
      toast.error("Failed to delete avatar");
      console.error(err);
    }
  };

  const handleEditMask = (avatar: Avatar) => {
    setEditingAvatar(avatar);
  };

  const handleSaveMask = async (updatedAvatar: Avatar) => {
    try {
      const response = await api.patch(`/api/avatars/${updatedAvatar.id}`, {
        x: updatedAvatar.x,
        y: updatedAvatar.y,
        width: updatedAvatar.width,
        height: updatedAvatar.height,
      });

      if (response.data.success) {
        // Update the avatar in the state
        setAvatars(
          avatars.map((avatar) =>
            avatar.id === updatedAvatar.id ? updatedAvatar : avatar
          )
        );

        toast.success("Mask updated successfully");
        setEditingAvatar(null); // Close the editor

        // Refresh to ensure we have the latest data
        fetchAvatars();
      } else {
        throw new Error(response.data.message || "Failed to update mask");
      }
    } catch (err) {
      console.error("Error saving mask:", err);
      toast.error("Failed to update mask");
    }
  };

  if (loading && avatars.length === 0) {
    return (
      <div className="flex justify-center items-center h-[80vh] bg-white dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen">
      <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
        Avatar Management
      </h1>

      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Type filter selector */}
      <div className="flex flex-col md:flex-row md:justify-between mb-4 gap-4">
        <div className="w-full md:w-64">
          <label
            htmlFor="type-filter"
            className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
          >
            Avatar Type:
          </label>
          <select
            id="type-filter"
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              setPage(1); // Reset to first page when changing type
            }}
            className="w-full border rounded px-3 py-2 text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            {typeOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        {/* Items per page selector */}
        <div className="flex items-center">
          <label
            htmlFor="limit-select"
            className="mr-2 text-sm text-gray-700 dark:text-gray-300"
          >
            Items per page:
          </label>
          <select
            id="limit-select"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1); // Reset to first page when changing limit
            }}
            className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={96}>96</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {avatars.map((avatar) => (
          <div
            key={avatar.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700"
          >
            <div className="relative aspect-[9/16] overflow-hidden bg-gray-100 dark:bg-gray-700">
              <div className="absolute top-2 right-2 flex space-x-1 z-10">
                <button
                  className="bg-white dark:bg-gray-800 bg-opacity-70 dark:bg-opacity-70 hover:bg-red-100 dark:hover:bg-red-900 p-1 rounded-full"
                  onClick={() => handleDelete(avatar.id)}
                >
                  <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                </button>

                {/* Only show edit button for product type avatars */}
                {avatar.item_type === "product" && (
                  <button
                    className="bg-white dark:bg-gray-800 bg-opacity-70 dark:bg-opacity-70 hover:bg-blue-100 dark:hover:bg-blue-900 p-1 rounded-full"
                    onClick={() => handleEditMask(avatar)}
                  >
                    <PencilSquareIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </button>
                )}
              </div>

              <div
                className="w-full h-full cursor-pointer"
                onClick={() =>
                  avatar.item_type === "product" && handleEditMask(avatar)
                }
              >
                <img
                  className="w-full h-full object-cover bg-gray-50 dark:bg-gray-800"
                  src={avatar.preview_image_url || avatar.character_image_url}
                  alt={avatar.name}
                />

                {/* Bounding box visualization */}
                {avatar.item_type === "product" && (
                  <div
                    className="absolute border-2 border-purple-500 dark:border-purple-400 pointer-events-none overflow-visible"
                    style={{
                      ...calculateBoundingBoxStyle(avatar),
                      backgroundColor: "rgba(168, 85, 247, 0.4)",
                    }}
                  ></div>
                )}
              </div>
            </div>

            <div className="p-3">
              <p className="text-sm font-medium truncate mb-2 text-gray-900 dark:text-white">
                {avatar.name}
              </p>

              <div className="mt-1">
                <Listbox
                  value={
                    changedAvatars[avatar.id] !== undefined
                      ? changedAvatars[avatar.id]
                      : avatar.item_type
                  }
                  onChange={(value) => handleTypeChange(avatar.id, value)}
                >
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-700 bg-opacity-80 py-1 pl-2 pr-8 text-left border border-gray-200 dark:border-gray-600 focus:outline-none focus-visible:border-blue-500 dark:focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400 text-xs text-gray-900 dark:text-white">
                      <span className="block truncate">
                        {typeOptions.find(
                          (option) =>
                            option.id ===
                            (changedAvatars[avatar.id] || avatar.item_type)
                        )?.name || "Select type"}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
                        <ChevronUpDownIcon
                          className="h-4 w-4 text-gray-400 dark:text-gray-500"
                          aria-hidden="true"
                        />
                      </span>
                    </Listbox.Button>
                    <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-opacity-20 focus:outline-none z-50">
                      {typeOptions.map((option) => (
                        <Listbox.Option
                          key={option.id}
                          className={({ active }) =>
                            `relative cursor-default select-none py-1 pl-8 pr-2 ${
                              active
                                ? "bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100"
                                : "text-gray-900 dark:text-white"
                            }`
                          }
                          value={option.id}
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block truncate ${
                                  selected ? "font-medium" : "font-normal"
                                }`}
                              >
                                {option.name}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-blue-600 dark:text-blue-400">
                                  <CheckIcon
                                    className="h-3 w-3"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>

                {changedAvatars[avatar.id] !== undefined && (
                  <button
                    className="flex items-center justify-center w-full mt-2 px-3 py-1.5 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    onClick={() => handleSave(avatar.id)}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {avatars.length === 0 && !loading && (
        <div className="flex justify-center mt-8">
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            No avatars found
          </p>
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex justify-center items-center mt-8">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className={`mx-1 px-4 py-2 rounded border ${
            page === 1
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-gray-700"
              : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
          }`}
        >
          Previous
        </button>
        <span className="mx-4 text-gray-700 dark:text-gray-300">
          Page {page} of {totalPages || 1}
        </span>
        <button
          disabled={page === totalPages || totalPages === 0}
          onClick={() => setPage((p) => p + 1)}
          className={`mx-1 px-4 py-2 rounded border ${
            page === totalPages || totalPages === 0
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed border-gray-200 dark:border-gray-700"
              : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
          }`}
        >
          Next
        </button>
      </div>

      {/* Mask editor modal */}
      {editingAvatar && (
        <MaskEditorModal
          avatar={editingAvatar}
          onClose={() => setEditingAvatar(null)}
          onSave={handleSaveMask}
        />
      )}
    </div>
  );
};

export default ViewAvatarsPage;
